import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import type { Subscription } from '@/types';
import { Layout } from '@/components/Layout';
import { useAuthStore } from '@/stores/authStore';

type Tab = 'profile' | 'billing' | 'data';

export function SettingsPage() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [user, setUser] = useState<{ email: string; name: string } | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [userResponse, subscriptionResponse] = await Promise.all([
          api.auth.me(),
          api.getSubscription(),
        ]);
        setUser(userResponse.data.user);
        setSubscription(subscriptionResponse.data);
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const response = await api.createCheckout({ tier: 'pro' });
      window.location.href = response.data.checkoutUrl;
    } catch (err) {
      console.error('Failed to create checkout:', err);
      setUpgrading(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await api.exportUserData();
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vistora-data-${Date.now()}.json`;

      // Attach to DOM for better browser compatibility
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
      alert('Your data has been exported successfully');
    } catch (err) {
      console.error('Failed to export data:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmInput !== 'DELETE') {
      return;
    }

    setDeleting(true);
    try {
      await api.deleteAccount();
      clearAuth();
      navigate('/');
    } catch (err: any) {
      console.error('Failed to delete account:', err);
      // Check if it's the subscription error
      const errorMessage = err?.message || 'Failed to delete account. Please try again or contact support.';
      alert(errorMessage);
      setDeleting(false);
      // Reset the confirmation state so user can try again after canceling subscription
      setDeleteConfirmInput('');
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  const isFreeTier = !subscription || subscription.tier === 'free';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account and subscription</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'billing'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Billing & Subscription
              </button>
              <button
                onClick={() => setActiveTab('data')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'data'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Data & Privacy
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={user?.name || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>

                <p className="text-sm text-gray-500">
                  Profile editing coming soon. Contact support to update your information.
                </p>
              </div>
            )}

            {activeTab === 'billing' && (
              <div className="space-y-6">
                {/* Current Plan */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h3>
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-gray-900 capitalize">
                          {subscription?.tier || 'Free'} Plan
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {isFreeTier ? (
                            'Limited to 3 SOWs'
                          ) : (
                            <>
                              Unlimited SOWs • $10/month
                              {subscription?.status && (
                                <span className="ml-2 capitalize">({subscription.status})</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {isFreeTier && (
                        <button
                          onClick={handleUpgrade}
                          disabled={upgrading}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                          {upgrading ? 'Processing...' : 'Upgrade to Pro'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pro Plan Benefits */}
                {isFreeTier && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Pro Plan Benefits
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <svg
                            className="w-5 h-5 text-blue-600 mt-0.5 mr-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div>
                            <div className="font-medium text-gray-900">Unlimited SOWs</div>
                            <div className="text-sm text-gray-600">
                              Create as many statements of work as you need
                            </div>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <svg
                            className="w-5 h-5 text-blue-600 mt-0.5 mr-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div>
                            <div className="font-medium text-gray-900">Early Bird Pricing</div>
                            <div className="text-sm text-gray-600">
                              <span className="line-through">$15/month</span>{' '}
                              <span className="text-blue-600 font-semibold">$10/month</span> for first 6 months
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Subscription Details */}
                {!isFreeTier && subscription && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Subscription Details
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Status</span>
                        <span className="font-medium text-gray-900 capitalize">
                          {subscription.status}
                        </span>
                      </div>
                      {subscription.currentPeriodEnd && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Next billing date</span>
                          <span className="font-medium text-gray-900">
                            {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={async () => {
                            try {
                              const response = await api.getCustomerPortalUrl();
                              window.location.href = response.data.url;
                            } catch (err) {
                              console.error('Failed to open customer portal:', err);
                              alert('Failed to open subscription management. Please try again.');
                            }
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Manage subscription
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'data' && (
              <div className="space-y-6">
                {/* Export Data Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Export Your Data</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Download a copy of all your data in JSON format, including your profile, SOWs, and subscription information.
                  </p>
                  <button
                    onClick={handleExportData}
                    disabled={exporting}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {exporting ? 'Exporting...' : 'Export My Data'}
                  </button>
                </div>

                {/* Delete Account Section */}
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Account</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Permanently delete your Vistora account. This action cannot be undone.
                  </p>

                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      Delete My Account
                    </button>
                  ) : (
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Are you absolutely sure?</h4>
                        {subscription && subscription.tier !== 'free' && subscription.status !== 'canceled' ? (
                          <div className="bg-yellow-50 border border-yellow-300 rounded-md p-4 mb-4">
                            <p className="text-sm font-semibold text-yellow-900 mb-2">⚠️ Subscription Must Be Canceled</p>
                            <p className="text-sm text-yellow-800 mb-3">
                              You have an active billing relationship ({subscription.status}). Please cancel your subscription in the Billing tab before deleting your account.
                            </p>
                            <button
                              onClick={() => {
                                setShowDeleteConfirm(false);
                                setDeleteConfirmInput('');
                                setActiveTab('billing');
                              }}
                              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium"
                            >
                              Go to Billing
                            </button>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-700 space-y-2">
                            <p>• Your account will be deleted and you will no longer be able to login</p>
                            <p>• Your SOWs will remain accessible to clients via their access links</p>
                            <p>• Your personal information (name, email) will be retained for legal and contractual purposes as required by law</p>
                            <p>• This action cannot be undone</p>
                          </div>
                        )}
                      </div>
                      {!(subscription && subscription.tier !== 'free' && subscription.status !== 'canceled') && (
                        <>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                              Type <span className="font-bold">DELETE</span> to confirm
                            </label>
                            <input
                              type="text"
                              value={deleteConfirmInput}
                              onChange={(e) => setDeleteConfirmInput(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                              placeholder="DELETE"
                            />
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                setShowDeleteConfirm(false);
                                setDeleteConfirmInput('');
                              }}
                              disabled={deleting}
                              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 font-medium"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleDeleteAccount}
                              disabled={deleteConfirmInput !== 'DELETE' || deleting}
                              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                              {deleting ? 'Deleting...' : 'Delete My Account'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Privacy Info */}
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy & Data Retention</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    We take your privacy seriously. For details on how we handle your data, please review our{' '}
                    <a href="/privacy" className="text-blue-600 hover:text-blue-700 font-medium">
                      Privacy Policy
                    </a>
                    .
                  </p>
                  <p className="text-sm text-gray-600">
                    If you have questions about data retention or deletion, contact us at{' '}
                    <a href="mailto:privacy@vistora.app" className="text-blue-600 hover:text-blue-700 font-medium">
                      privacy@vistora.app
                    </a>
                    .
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
